import graphql__f4fe2d2e38d29cdd401b from "./__generated__/ComplexMenuDesktopNavigationItem_navigationItem.graphql.js";import { useRef, useCallback, useEffect } from "react";
import { graphql, useFragment } from "react-relay";
import classNames from "classnames";

import { useNavigationItemIsActive, useComplexMenuContext } from "../ComplexMenuContext";
import Icon from "../../../Utils/Icon";
import KEY_CODES from "../../../Utils/keyCodes";
import Button from "../../../Buttons/Button";
import ButtonLink from "../../../Buttons/ButtonLink";
import ComplexMenuDesktopVerticalSubmenu from "./ComplexMenuDesktopVerticalSubmenu";

const ComplexMenuDesktopNavigationItem = props => {
  const navigationItem = useFragment(
    ComplexMenuDesktopNavigationItem_navigationItem,
    props.navigationItem
  );

  const { id, name, url, iconClass, isSubMenu } = navigationItem;

  const itemRef = useRef(null);
  const buttonRef = useRef();
  const submenuRef = useRef(null);
  const { isActive } = useNavigationItemIsActive(navigationItem);
  const { hoveredSubMenuId, setHoveredSubMenuId } = useComplexMenuContext();

  const closeTimeoutRef = useRef(null);

  const isSubmenuOpen = hoveredSubMenuId === id;
  const showSubmenu = isSubMenu && isSubmenuOpen;

  // We wrap handlers to update global state
  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearCloseTimeout();
    };
  }, [clearCloseTimeout]);

  const handleMouseEnter = useCallback(() => {
    clearCloseTimeout();
    if (isSubMenu) {
      setHoveredSubMenuId(id);
    }
  }, [clearCloseTimeout, isSubMenu, id, setHoveredSubMenuId]);

  const handleMouseLeave = useCallback(
    e => {
      const nextTarget = e?.relatedTarget;
      if (itemRef.current && nextTarget && itemRef.current.contains(nextTarget)) return;

      clearCloseTimeout();
      closeTimeoutRef.current = setTimeout(() => {
        setHoveredSubMenuId(current => (current === id ? null : current));
      }, 200);
    },
    [clearCloseTimeout, id, setHoveredSubMenuId]
  );

  // Handle keyboard navigation for accessibility
  const handleKeyDown = useCallback(
    e => {
      if (!isSubMenu) return;

      if (e.keyCode === KEY_CODES.ENTER || e.keyCode === KEY_CODES.SPACE) {
        e.preventDefault();
        clearCloseTimeout();
        const nextIsOpen = !isSubmenuOpen;
        setHoveredSubMenuId(nextIsOpen ? id : null);
      }

      if (e.keyCode === KEY_CODES.ESCAPE && isSubmenuOpen) {
        e.preventDefault();
        clearCloseTimeout();
        setHoveredSubMenuId(null);
        buttonRef.current?.focus();
      }
    },
    [clearCloseTimeout, id, isSubmenuOpen, isSubMenu, setHoveredSubMenuId]
  );

  const buttonClassName = classNames(
    "btn",
    "btn-unstyled",
    "complex-menu-navigation-item-button",
    "text-primary-on-white-hover",
    {
      active: isActive
    }
  );

  return (
    <li
      ref={itemRef}
      className="complex-menu-navigation-item position-relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {isSubMenu && (
        <Button
          ref={buttonRef}
          className={buttonClassName}
          aria-expanded={showSubmenu}
          aria-haspopup
          onKeyDown={handleKeyDown}
        >
          <span className="button-inner">
            {iconClass && (
              <Icon className={classNames(iconClass, "button-icon-before")} />
            )}
            <span className="button-label">
              {name}
              <i className="fas fa-caret-down ml-1" aria-hidden="true" />
            </span>
          </span>
        </Button>
      )}

      {!isSubMenu && (
        <ButtonLink
          href={url}
          className={buttonClassName}
          iconClassName={iconClass}
          label={name}
        />
      )}

      {showSubmenu && (
        <ComplexMenuDesktopVerticalSubmenu
          ref={submenuRef}
          navigationItem={navigationItem}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        />
      )}
    </li>
  );
};

const ComplexMenuDesktopNavigationItem_navigationItem = graphql__f4fe2d2e38d29cdd401b;

export default ComplexMenuDesktopNavigationItem;
